import { Injectable, inject } from '@angular/core';
import { SwNavigatorService } from './sw-navigator.service';
import { PointDataService } from './point-data.service';
import { FilterService } from './filter.service';
import { RecyclingPoint } from '../../types/recycling-point.interface';
import { materialsState } from '../../state/materials.state';
import { GeocodingService } from './geocoding.service';
import { GeocodingResult } from './geocoding/geocoding-provider.interface';
import { MapLibrePosition } from '../../components/shared/maplibre-map/maplibre-map.component';
import { ScreenLayer } from '../enums/screen-layer.enum';

@Injectable({
  providedIn: 'root'
})
export class PointCreationService {
  private swNavigator = inject(SwNavigatorService);
  private pointDataService = inject(PointDataService);
  private filterService = inject(FilterService);
  private geocodingService = inject(GeocodingService);

  async startCreationFlow(coordinates?: MapLibrePosition): Promise<void> {
    const currentSearchTerm = this.filterService.searchTerm();
    const normalizedSearchTerm = currentSearchTerm?.trim?.() ?? '';
    const selectedMaterials = materialsState.selectedMaterials();
    const addressFromSearch = this.extractAddressFromSearch(normalizedSearchTerm);

    // When creating from map: leave name empty for geocoding to fill
    // When creating from search/button: use search term as name
    const initialName = coordinates ? '' : (normalizedSearchTerm || addressFromSearch.name || '');

    const blankPoint: RecyclingPoint = {
      id: '',
      name: initialName,
      entityType: '', // Leave empty - user must select from dropdown (required field)
      description: selectedMaterials.length > 0
        ? `Centro que recibe: ${selectedMaterials.map(material => material.label).join(', ')}`
        : '',
      latitude: coordinates?.lat ?? 0,
      longitude: coordinates?.lng ?? 0,
      position: coordinates ? { lat: coordinates.lat, lng: coordinates.lng } : undefined,
      contactPhone: '',
      contactEmail: '',
      website: '',
      address: {
        street: coordinates ? '' : (addressFromSearch.street || ''),
        fullAddress: coordinates ? '' : (addressFromSearch.fullAddress || normalizedSearchTerm),
        number: '',
        city: coordinates ? '' : (addressFromSearch.city || ''),
        province: '',
        postalCode: ''
      },
      schedules: [],
      materials: selectedMaterials.map(material => ({
        id: material.id,
        name: material.name || material.label,
        label: material.label,
        icon: material.icon,
        type: material.type || 'material',
        recyclable: material.recyclable ?? true
      })),
      services: {},
      accessibility: {}
    };

    if (coordinates) {
      const geocodedAddress = await this.tryReverseGeocode(coordinates.lat, coordinates.lng);
      
      console.log('🏗️ PointCreation: geocodedAddress received:', geocodedAddress);
      
      if (geocodedAddress) {
        const { address, display_name } = geocodedAddress;
        
        console.log('🏗️ PointCreation: display_name from provider:', display_name);
        console.log('🏗️ PointCreation: address object:', address);
        
        // Build clean street with number
        const streetName = address.street || address.road || '';
        const houseNum = address.houseNumber || address.house_number || '';
        const fullStreet = houseNum ? `${streetName} ${houseNum}` : streetName;
        
        // Get city - use ONLY city field, not county (county often duplicates city in Argentina)
        const city = address.city || '';
        
        console.log('🏗️ PointCreation: extracted values:', { streetName, houseNum, fullStreet, city });
        
        blankPoint.address = {
          street: fullStreet,
          fullAddress: display_name, // Use clean display_name from provider directly
          number: houseNum,
          city: city,
          province: address.state || '',
          postalCode: address.postalCode || address.postcode || ''
        };
        
        console.log('🏗️ PointCreation: final blankPoint.address:', blankPoint.address);

        // Set name to street name from geocoding
        blankPoint.name = streetName;
      }
    }

    this.pointDataService.setPointForEdit(blankPoint);

    this.swNavigator.navigateObj({
      path: '/new-point',
      back_path: '/home',
      layer: ScreenLayer.Edit
    });
  }

  private async tryReverseGeocode(lat: number, lng: number): Promise<GeocodingResult | null> {
    try {
      return await this.geocodingService.reverseGeocode({ lat, lng });
    } catch (error) {
      console.error('PointCreationService: reverse geocode failed', error);
      return null;
    }
  }

  private extractAddressFromSearch(searchTerm: string): {
    name: string;
    street: string;
    city: string;
    fullAddress: string;
  } {
    if (!searchTerm || searchTerm.trim().length === 0) {
      return { name: '', street: '', city: '', fullAddress: '' };
    }

    const trimmedSearch = searchTerm.trim();
    const addressIndicators = /\b(calle|av|avenida|boulevard|bv|blvd|street|st|road|rd)\b/i;
    const numberPattern = /\b\d+\b/;

    if (addressIndicators.test(trimmedSearch) || numberPattern.test(trimmedSearch)) {
      return {
        name: '',
        street: trimmedSearch,
        city: '',
        fullAddress: trimmedSearch
      };
    }

    return {
      name: trimmedSearch,
      street: '',
      city: '',
      fullAddress: trimmedSearch
    };
  }
}
